import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { TermsContactEmail } from "./-terms.contact";
import { TERMS_CLOSING_SECTIONS } from "./-terms.sections.closing";

export type TermsBlock = { paragraphs?: ReactNode[]; list?: readonly ReactNode[] };

export type TermsSectionContent = { title: string; blocks: TermsBlock[] };

export const TERMS_SECTIONS: TermsSectionContent[] = [
  {
    title: "1. Acceptance of terms",
    blocks: [
      {
        paragraphs: [
          <>
            By accessing or using Daily Wars (&quot;the Service&quot;), you agree to be bound by
            these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, do not
            use the Service. These Terms form a legally binding agreement between you and Elias
            Brange, operating Daily Wars.
          </>,
        ],
      },
    ],
  },
  {
    title: "2. Description of service",
    blocks: [
      {
        paragraphs: [
          <>
            Daily Wars is a daily turn-based strategy game. The Service lets you play tactical maps,
            submit turns, track scores and streaks, view leaderboards, and—when signed in—maintain a
            player profile. Some features require an account; you may also play as a guest with
            limited persistence.
          </>,
        ],
      },
    ],
  },
  {
    title: "3. User accounts",
    blocks: [
      {
        paragraphs: [
          <>
            Certain features require an account. You are responsible for maintaining the
            confidentiality of your credentials, for all activity under your account, and for
            notifying us promptly of any unauthorized use.
          </>,
          <>
            You must be at least 16 years old to create an account. By creating an account, you
            represent that you meet this age requirement, as described in our{" "}
            <Link
              to="/privacy-policy"
              className="text-primary underline underline-offset-4 hover:text-foreground"
            >
              Privacy policy
            </Link>
            .
          </>,
        ],
      },
    ],
  },
  {
    title: "4. Subscription and payments",
    blocks: [
      {
        paragraphs: [
          <>
            The Service offers free play and an optional paid Premium tier with extra maps and
            archive access. Premium subscriptions are sold and billed by Polar as Merchant of Record
            on our behalf. Your purchase contract for Premium is between you and Polar, not Elias
            Brange operating as Daily Wars. We receive subscription status from Polar to unlock
            Premium in the Service; we do not receive or store your full payment card numbers.
          </>,
        ],
      },
      {
        paragraphs: [<>When you subscribe to Premium:</>],
        list: [
          "Checkout shows the price, billing interval, trial terms (if any), renewal terms, and how to cancel. Those disclosures govern billing.",
          "Subscriptions renew automatically each billing period unless you cancel before renewal, as shown at checkout and in Polar's customer portal.",
          "You may cancel through billing management in the Service (which links to Polar). Cancellation typically takes effect at the end of the current billing period unless checkout states otherwise.",
          <>
            Billing, taxes, refunds, chargebacks, and consumer-law rights relating to the purchase
            (including any EU or UK withdrawal rights) are handled by Polar under{" "}
            <a
              href="https://polar.sh/legal/terms"
              className="text-primary underline underline-offset-4 hover:text-foreground"
              rel="noopener noreferrer"
              target="_blank"
            >
              Polar&apos;s terms
            </a>{" "}
            and refund policy. Contact <TermsContactEmail /> for purchase or refund questions; we
            will assist or direct you to Polar.
          </>,
        ],
      },
    ],
  },
  {
    title: "5. Acceptable use",
    blocks: [
      {
        paragraphs: [<>You agree not to:</>],
        list: [
          "Use the Service for any illegal purpose",
          "Attempt unauthorized access to the Service or its systems",
          "Interfere with or disrupt the Service or servers",
          "Upload malicious code or content",
          "Impersonate any person or entity",
          "Use the Service to spam, harass, or abuse others",
          "Use automated means (bots, scrapers) to access the Service without permission",
          "Use offensive, discriminatory, hateful, or otherwise inappropriate display names or other profile content shown to other players",
        ],
      },
    ],
  },
  {
    title: "6. Gameplay data and intellectual property",
    blocks: [
      {
        paragraphs: [
          <>
            You retain any rights you have in content you provide (such as a display name). By using
            the Service, you grant us a limited, non-exclusive license to store, process, and
            display your gameplay data, profile, and scores as needed to operate the Service,
            including leaderboards.
          </>,
          <>
            The Service—including its design, code, maps, branding, and documentation—is owned by
            Elias Brange, operating as Daily Wars, and protected by intellectual property laws. You
            may not copy, modify, or distribute any part of the Service without our written consent,
            except where applicable law gives you a non-waivable right to do so (such as
            decompilation for interoperability under EU law).
          </>,
        ],
      },
    ],
  },
  {
    title: "7. Service tiers and changes",
    blocks: [
      {
        paragraphs: [
          <>
            Free, guest, and Premium tiers may differ in features and limits. We may change tiers,
            features, or limits with reasonable notice. If a change materially reduces features of a
            paid plan you are on, you may cancel your subscription.
          </>,
        ],
      },
    ],
  },
  {
    title: "8. Account termination",
    blocks: [
      {
        paragraphs: [
          <>
            We may suspend or terminate your access for violation of these Terms or for other
            reasons, with notice where practicable. If we terminate your account without cause while
            you have prepaid Premium time remaining, we will refund the unused portion or extend
            access, whichever we choose in good faith. You may delete your account through account
            settings where available. Upon deletion, personal data is handled as described in our{" "}
            <Link
              to="/privacy-policy"
              className="text-primary underline underline-offset-4 hover:text-foreground"
            >
              Privacy policy
            </Link>
            .
          </>,
        ],
      },
    ],
  },
  {
    title: "9. Disclaimer of warranties",
    blocks: [
      {
        paragraphs: [
          <>
            The Service is provided &quot;as is&quot; and &quot;as available&quot; without
            warranties of any kind, express or implied, including merchantability, fitness for a
            particular purpose, and non-infringement. We do not guarantee that the Service will be
            uninterrupted, secure, or error-free.
          </>,
        ],
      },
    ],
  },
  {
    title: "10. Limitation of liability",
    blocks: [
      {
        paragraphs: [
          <>
            To the maximum extent permitted by law, Daily Wars shall not be liable for indirect,
            incidental, special, consequential, or punitive damages arising from your use of or
            inability to use the Service. Our total liability for any claim related to the Service
            shall not exceed the amount you paid us in the 12 months before the claim, or $50 USD,
            whichever is greater. Nothing in these Terms limits liability that cannot be limited
            under applicable mandatory law, including liability for death or personal injury caused
            by negligence, fraud, or other rights that consumer protection law does not allow to be
            excluded.
          </>,
        ],
      },
    ],
  },
  ...TERMS_CLOSING_SECTIONS,
];
